import { CatalogItemPrice } from "@common/types/catalogItem";
import { CatalogItemPriceOperation } from "@common/schemas/domains/catalogItem/catalogItemPrice.schema";
import { compareByRecencyDesc } from "@common/helpers/catalogItemPrice";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";

export enum CatalogItemPriceColumn {
    PRICE = "price",
    DESCRIPTION = "description",
    CREATED_ON = "createdOn",
    STATUS = "status",
    ACTIONS = "actions",
}

export const DRAFT_PRICE_ID = "draft-price";

export interface CatalogItemPriceRow extends CatalogItemPrice {
    isLastActive: boolean;
    isDraft?: boolean;
    // A price added via "Add price" but not yet persisted - staged locally
    // until the parent CatalogItemFormModal's Save commits every pending
    // price operation together with the item's own field edits. Distinct
    // from `isDraft`, which marks the single in-progress input row.
    isStaged?: boolean;
}

export interface CatalogItemPricesTableCallbacks {
    onSetDefault: (price: CatalogItemPriceRow) => void;
    onArchiveToggle: (price: CatalogItemPriceRow) => void;
    onDelete: (price: CatalogItemPriceRow) => void;
    onCommitDraft: (unitAmount: number) => Promise<void>;
    onCancelDraft: () => void;
    // Discards a locally-staged (not-yet-created) price row before Save.
    onRemoveStaged: (clientId: string) => void;
    // Stages a description edit locally - nothing is sent to the API until
    // the parent modal's Save. Returns a promise (always resolving true) only
    // to match PriceDescriptionCell's existing async handler shape.
    onUpdateDescription: (
        price: CatalogItemPriceRow,
        description: string | null,
    ) => Promise<boolean>;
}

export interface CatalogItemPricesViewOptions {
    // True while the in-progress draft row is being staged, or while the
    // parent modal's Save is in flight. Disables the add-row input/cancel
    // button and staged-row remove buttons so the list can't be mutated
    // mid-commit.
    isDraftPending: boolean;
    // Focus the draft amount after the user clicks Add price.
    shouldFocusDraft: boolean;
}

export type CatalogItemPricesTableMeta = ViewTableMeta<
    CatalogItemPricesTableCallbacks,
    CatalogItemPricesViewOptions
>;

export function buildDraftPriceRow(
    catalogItemId: string,
    currency: string,
): CatalogItemPriceRow {
    return {
        id: DRAFT_PRICE_ID,
        catalogItemId,
        unitAmount: 0,
        currency,
        active: true,
        isDefault: false,
        stripePriceId: null,
        description: null,
        createdAt: new Date(0),
        updatedAt: new Date(0),
        isLastActive: false,
        isDraft: true,
    };
}

function sortPersistedPrices<T extends CatalogItemPrice>(prices: T[]): T[] {
    return [...prices].toSorted((left, right) => {
        if (left.isDefault !== right.isDefault) {
            return left.isDefault ? -1 : 1;
        }
        if (left.active !== right.active) {
            return left.active ? -1 : 1;
        }
        return compareByRecencyDesc(left, right);
    });
}

export function buildCatalogItemPriceRows(
    prices: CatalogItemPrice[],
): CatalogItemPriceRow[] {
    const activeCount = prices.filter((price) => price.active).length;
    return sortPersistedPrices(prices).map((price) => ({
        ...price,
        isLastActive: price.active && activeCount <= 1,
    }));
}

// --- Pending price operations (local-only staging) ---
//
// Every price create/archive/enable/delete/set-default/description edit is
// staged as a CatalogItemPriceOperation in local component state
// (CatalogItemPricesTable) rather than calling any API. The parent modal's
// Save reads the full pending list and includes it as updateCatalogItem's
// `prices` field, so item field edits and every staged price change commit
// together, atomically, or not at all - matching how archive/delete/other
// item edits already behave. There is intentionally no way to persist a
// price change independently of that Save.

// Applies pending operations on top of the item's persisted prices to
// project what the price list would look like after Save, so the table can
// render a single consistent working set (used by buildWorkingPriceRows).
// Deleted persisted prices are dropped; staged creates are appended (in
// creation order) with a `clientId`-derived id and isStaged: true.
function applyPendingPriceOperations(
    catalogItemId: string,
    prices: CatalogItemPrice[],
    operations: CatalogItemPriceOperation[],
): CatalogItemPriceRow[] {
    let working: CatalogItemPriceRow[] = prices.map((price) => ({
        ...price,
        isLastActive: false, // recomputed below once the full working set is known
    }));

    for (const operation of operations) {
        if (operation.op === "create") {
            const newPrice: CatalogItemPriceRow = {
                id: operation.clientId,
                catalogItemId,
                unitAmount: operation.unitAmount,
                currency: operation.currency,
                active: true,
                isDefault: Boolean(operation.isDefault),
                stripePriceId: null,
                description: operation.description ?? null,
                createdAt: new Date(0),
                updatedAt: new Date(0),
                isLastActive: false,
                isStaged: true,
            };
            working = operation.isDefault
                ? [
                      ...working.map((price) => ({
                          ...price,
                          isDefault: false,
                      })),
                      newPrice,
                  ]
                : [...working, newPrice];
            continue;
        }

        if (operation.op === "delete") {
            working = working.filter((price) => price.id !== operation.priceId);
            continue;
        }

        // op === "update"
        working = working.map((price) => {
            if (price.id !== operation.priceId) {
                return operation.isDefault
                    ? { ...price, isDefault: false }
                    : price;
            }
            return {
                ...price,
                active: operation.active ?? price.active,
                isDefault: operation.isDefault ? true : price.isDefault,
                description:
                    operation.description !== undefined
                        ? operation.description
                        : price.description,
            };
        });
    }

    const activeCount = working.filter((price) => price.active).length;
    return working.map((price) => ({
        ...price,
        isLastActive: price.active && activeCount <= 1,
    }));
}

// Builds the rows CatalogItemPricesTable renders: persisted prices (sorted
// the same way buildCatalogItemPriceRows always has) with every pending
// operation applied, plus any staged (not-yet-created) rows appended at the
// end - matching the table's pre-existing visual convention of always
// showing staged rows last, regardless of their active/default state.
export function buildWorkingPriceRows(
    catalogItemId: string,
    prices: CatalogItemPrice[],
    operations: CatalogItemPriceOperation[],
): CatalogItemPriceRow[] {
    const working = applyPendingPriceOperations(
        catalogItemId,
        prices,
        operations,
    );
    const persisted = working.filter((price) => !price.isStaged);
    const staged = working.filter((price) => price.isStaged);
    return [...sortPersistedPrices(persisted), ...staged];
}

// Stages a new price. Appended as a "create" op with a client-generated id;
// nothing is sent to the API until Save reads the full operations list.
export function addCreatePriceOperation(
    operations: CatalogItemPriceOperation[],
    unitAmount: number,
    currency: string,
): CatalogItemPriceOperation[] {
    return [
        ...operations,
        {
            op: "create",
            clientId: crypto.randomUUID(),
            unitAmount,
            currency,
        },
    ];
}

// Discards a staged (not-yet-created) price before Save.
export function removeCreatePriceOperation(
    operations: CatalogItemPriceOperation[],
    clientId: string,
): CatalogItemPriceOperation[] {
    return operations.filter(
        (operation) =>
            !(operation.op === "create" && operation.clientId === clientId),
    );
}

// Edits a staged (not-yet-created) price's description in place, mutating
// its pending "create" op rather than adding a separate operation.
export function updateCreatePriceOperationDescription(
    operations: CatalogItemPriceOperation[],
    clientId: string,
    description: string | null,
): CatalogItemPriceOperation[] {
    return operations.map((operation) =>
        operation.op === "create" && operation.clientId === clientId
            ? { ...operation, description }
            : operation,
    );
}

// Stages an archive/enable, set-default, and/or description edit for a
// persisted price, merging into any existing pending "update" op for the
// same priceId so a batch never carries two update ops for one price.
export function mergeUpdatePriceOperation(
    operations: CatalogItemPriceOperation[],
    priceId: string,
    patch: {
        active?: boolean;
        isDefault?: true;
        description?: string | null;
    },
): CatalogItemPriceOperation[] {
    const existingIndex = operations.findIndex(
        (operation) =>
            operation.op === "update" && operation.priceId === priceId,
    );
    if (existingIndex === -1) {
        return [...operations, { op: "update", priceId, ...patch }];
    }
    const merged = [...operations];
    const existing = merged[existingIndex];
    merged[existingIndex] =
        existing.op === "update" ? { ...existing, ...patch } : existing;
    return merged;
}

// Stages a delete for a persisted price. Drops any pending "update" op for
// the same priceId (moot once the price is being deleted) so the batch never
// sends a stale update alongside a delete for the same id.
export function addDeletePriceOperation(
    operations: CatalogItemPriceOperation[],
    priceId: string,
): CatalogItemPriceOperation[] {
    const withoutStaleUpdate = operations.filter(
        (operation) =>
            !(operation.op === "update" && operation.priceId === priceId),
    );
    return [...withoutStaleUpdate, { op: "delete", priceId }];
}

// A lone remaining active price is always the item's effective default, even
// if `isDefault` was never explicitly flipped in the DB (e.g. an item created
// before the multi-price feature). getCurrentActivePrice/
// getCurrentActivePriceForCurrency already fall back to "the only active
// price" when no row has isDefault set, so this is a display/action-lock-only
// distinction — it doesn't require a backend flag flip to stay correct
// end-to-end.
export function getPriceActionAvailability(price: CatalogItemPriceRow): {
    canSetDefault: boolean;
    canArchive: boolean;
    canUnarchive: boolean;
    canDelete: boolean;
} {
    if (price.isDraft || price.isStaged) {
        return {
            canSetDefault: false,
            canArchive: false,
            canUnarchive: false,
            canDelete: false,
        };
    }
    return {
        canSetDefault: price.active && !price.isDefault && !price.isLastActive,
        canArchive: price.active && !price.isDefault && !price.isLastActive,
        canUnarchive: !price.active,
        canDelete: !price.isDefault && !price.isLastActive,
    };
}

export function getSetDefaultDisabledReason(
    price: CatalogItemPriceRow,
): string | undefined {
    if (price.isDefault || price.isLastActive) {
        return "Already the default";
    }
    if (!price.active) {
        return "Unarchive first";
    }
    return undefined;
}

export function getKeepPriceDisabledReason(
    price: CatalogItemPriceRow,
): string | undefined {
    if (price.isDefault) {
        return "Set a different price as default first";
    }
    if (price.isLastActive) {
        return "Item must keep one active price";
    }
    return undefined;
}
